import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Loader2, Plus, Download, Trash2 } from "lucide-react";
import { createExpense, deleteExpense, listExpenses, listProjectsForInvoicing } from "@/lib/accounting/accounting.functions";
import { downloadCSV, fmtDate, fmtMoney, toCSV } from "@/lib/accounting/exports";
import { toast } from "sonner";

export const Route = createFileRoute("/_authenticated/admin/accounting/expenses")({
  component: Expenses,
});

const CATS = ["paint", "materials", "labor", "subcontractor", "equipment", "travel", "marketing", "software", "other"] as const;

function Expenses() {
  const listFn = useServerFn(listExpenses);
  const projectsFn = useServerFn(listProjectsForInvoicing);
  const createFn = useServerFn(createExpense);
  const delFn = useServerFn(deleteExpense);
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ["accounting", "expenses"], queryFn: () => listFn() });
  const projectsQ = useQuery({ queryKey: ["accounting", "projects-mini"], queryFn: () => projectsFn() });
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ project_id: "", category: "materials" as typeof CATS[number], vendor: "", amount: 0, expense_date: new Date().toISOString().slice(0, 10), notes: "" });
  const [confirmDel, setConfirmDel] = useState<{ id: string; label: string } | null>(null);
  const [deleting, setDeleting] = useState(false);

  const refresh = () => qc.invalidateQueries({ queryKey: ["accounting", "expenses"] });

  const save = async () => {
    try {
      await createFn({ data: { ...form, project_id: form.project_id || null, vendor: form.vendor || null, notes: form.notes || null } });
      toast.success("Expense added"); setOpen(false); refresh();
    } catch (e) { toast.error((e as Error).message); }
  };
  const confirmDelete = async () => {
    if (!confirmDel) return;
    setDeleting(true);
    try {
      await delFn({ data: { id: confirmDel.id } });
      toast.success("Expense deleted");
      setConfirmDel(null);
      refresh();
    } catch (e) { toast.error((e as Error).message); }
    finally { setDeleting(false); }
  };
  const exportCsv = () => {
    downloadCSV(`expenses_${new Date().toISOString().slice(0, 10)}.csv`, toCSV((data ?? []).map((e: any) => ({
      date: e.expense_date, vendor: e.vendor, category: e.category, job: e.customer_projects?.project_name, amount: e.amount, notes: e.notes,
    }))));
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-end gap-2">
        <button onClick={exportCsv} className="flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-white/10 text-xs text-zinc-200"><Download className="size-3.5" /> CSV</button>
        <button onClick={() => setOpen(true)} className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-amber-300 text-zinc-950 text-xs font-semibold"><Plus className="size-3.5" /> Add expense</button>
      </div>
      {isLoading ? (
        <div className="py-20 grid place-items-center"><Loader2 className="size-6 animate-spin text-amber-300" /></div>
      ) : (
        <div className="rounded-2xl border border-white/10 bg-white/[0.02] overflow-x-auto">
          <table className="w-full text-sm min-w-[800px]">
            <thead className="text-[10px] uppercase tracking-wider text-zinc-500 border-b border-white/10">
              <tr><th className="text-left px-4 py-3">Date</th><th className="text-left px-4 py-3">Vendor</th><th className="text-left px-4 py-3">Category</th><th className="text-left px-4 py-3">Job</th><th className="text-right px-4 py-3">Amount</th><th /></tr>
            </thead>
            <tbody>
              {(data ?? []).length === 0 ? (
                <tr><td colSpan={6} className="text-center py-12 text-zinc-500">No expenses yet.</td></tr>
              ) : (data ?? []).map((e: any) => (
                <tr key={e.id} className="border-b border-white/5">
                  <td className="px-4 py-3 text-zinc-300 text-xs">{fmtDate(e.expense_date)}</td>
                  <td className="px-4 py-3 text-white">{e.vendor ?? "—"}</td>
                  <td className="px-4 py-3 text-zinc-300 text-xs capitalize">{e.category}</td>
                  <td className="px-4 py-3 text-zinc-300">{e.customer_projects?.project_name ?? "—"}</td>
                  <td className="px-4 py-3 text-right text-white">{fmtMoney(e.amount)}</td>
                  <td className="px-4 py-3 text-right"><button onClick={() => setConfirmDel({ id: e.id, label: [fmtDate(e.expense_date), e.vendor, fmtMoney(e.amount)].filter(Boolean).join(" · ") })} className="text-zinc-500 hover:text-red-400"><Trash2 className="size-4" /></button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {open && (
        <div className="fixed inset-0 z-50 bg-black/70 grid place-items-center p-4" onClick={() => setOpen(false)}>
          <div className="bg-[#0a0a0a] border border-white/10 rounded-2xl p-5 max-w-md w-full space-y-3" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-serif text-lg text-white">Add expense</h3>
            <div className="grid grid-cols-2 gap-3">
              <label className="block"><span className="text-[10px] uppercase text-zinc-500 block mb-1">Date</span><input type="date" value={form.expense_date} onChange={(e) => setForm({ ...form, expense_date: e.target.value })} className={inp} /></label>
              <label className="block"><span className="text-[10px] uppercase text-zinc-500 block mb-1">Amount</span><input type="number" step="0.01" value={form.amount || ""} onChange={(e) => setForm({ ...form, amount: Number(e.target.value) })} className={inp} /></label>
            </div>
            <label className="block"><span className="text-[10px] uppercase text-zinc-500 block mb-1">Vendor</span><input value={form.vendor} onChange={(e) => setForm({ ...form, vendor: e.target.value })} className={inp} /></label>
            <div className="grid grid-cols-2 gap-3">
              <label className="block"><span className="text-[10px] uppercase text-zinc-500 block mb-1">Category</span>
                <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value as any })} className={inp}>
                  {CATS.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </label>
              <label className="block"><span className="text-[10px] uppercase text-zinc-500 block mb-1">Job</span>
                <select value={form.project_id} onChange={(e) => setForm({ ...form, project_id: e.target.value })} className={inp}>
                  <option value="">— None —</option>
                  {(projectsQ.data ?? []).map((p: any) => <option key={p.id} value={p.id}>{p.customer_name} — {p.project_name}</option>)}
                </select>
              </label>
            </div>
            <label className="block"><span className="text-[10px] uppercase text-zinc-500 block mb-1">Notes</span><textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} className={inp} /></label>
            <div className="flex justify-end gap-2">
              <button onClick={() => setOpen(false)} className="text-xs px-4 py-2 rounded-full border border-white/10 text-zinc-300">Cancel</button>
              <button onClick={save} className="text-xs px-4 py-2 rounded-full bg-amber-300 text-zinc-950 font-semibold">Save</button>
            </div>
          </div>
        </div>
      )}

      {confirmDel && (
        <div className="fixed inset-0 z-50 bg-black/70 grid place-items-center p-4" onClick={() => !deleting && setConfirmDel(null)}>
          <div className="bg-[#0a0a0a] border border-white/10 rounded-2xl p-5 max-w-sm w-full space-y-3" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-serif text-lg text-white">Delete expense?</h3>
            <p className="text-sm text-zinc-400">{confirmDel.label}</p>
            <p className="text-xs text-zinc-500">This cannot be undone.</p>
            <div className="flex justify-end gap-2 pt-1">
              <button onClick={() => setConfirmDel(null)} disabled={deleting} className="text-xs px-4 py-2 rounded-full border border-white/10 text-zinc-300 disabled:opacity-50">Cancel</button>
              <button onClick={confirmDelete} disabled={deleting} className="text-xs px-4 py-2 rounded-full bg-red-500 text-white font-semibold disabled:opacity-50 inline-flex items-center gap-1.5">
                {deleting && <Loader2 className="size-3.5 animate-spin" />} Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const inp = "w-full px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-sm text-white focus:outline-none focus:border-amber-300/40";